/*
 * utils.js
 *
 * Pure utility functions — no React, no JSX.
 * Exports: API constant, tier config helpers, fault diagnosis logic (getDiagnosis,
 * getComponents), timestamp formatters, Leaflet script loaders, and charger filter
 * helpers (markerStyle, chargerMatchesFilter).
 * Imported by: almost every other file in src/.
 */

export const API = process.env.REACT_APP_API_URL || "http://cloudpigeon:8000";

// ── Tier / priority config ────────────────────────────────────────────────────

export const TIER_CONFIG = {
  "P1": { color: "#EF4444", bg: "#FEF2F2", label: "P1", text: "High" },
  "P2": { color: "#F97316", bg: "#FFF7ED", label: "P2", text: "Medium" },
  "P3": { color: "#F59E0B", bg: "#FFFBEB", label: "P3", text: "Low" },
  "P4": { color: "#0EA5E9", bg: "#F0F9FF", label: "P4", text: "Healthy" },
};

export const getTier = (tier) => {
  if (!tier) return TIER_CONFIG["P4"];
  const key = tier.slice(0, 2);
  return TIER_CONFIG[key] || TIER_CONFIG["P4"];
};

export const getConfidence = (prob) => {
  if (prob > 0.85 || prob < 0.15) return { label: "HIGH CONF", bg: "#DBEAFE", color: "#1E40AF" };
  if ((prob >= 0.60 && prob <= 0.85) || (prob >= 0.15 && prob <= 0.40)) return { label: "MED CONF", bg: "#FEF3C7", color: "#92400E" };
  return { label: "LOW CONF", bg: "#F3F4F6", color: "#6B7280" };
};

export const getImpactLabel = (impact) => ({
  "H": "High severity — dispatch required",
  "M": "Medium severity — may self-resolve",
  "L": "Low severity — monitor",
  "N": "Informational — no dispatch required",
}[impact] || impact);

// ── Error code sets (Tritium hardware subcategories) ─────────────────────────

export const DC_DC_CODES      = new Set([630, 631, 632, 956, 961, 988, 993]);
export const THERMAL_CODES    = new Set([136, 137, 264, 358, 359, 547, 548, 573, 575]);
export const INVERTER_CODES   = new Set([277, 278, 297, 299, 300, 354]);
export const CONNECTOR_CODES  = new Set([544, 548, 559, 872, 873, 875, 877, 878]);
export const ISOLATION_CODES  = new Set([286, 287, 288, 293, 665, 666, 673, 675]);
export const PHYSICAL_CODES   = new Set([760, 761, 765, 766, 768, 770, 771, 772]);
export const isGridCode = (c) => c === 39 || (c >= 375 && c <= 378) || c === 385 || (c >= 500 && c <= 530);

// ── Fault diagnosis ───────────────────────────────────────────────────────────

export function getDiagnosis(charger) {
  const { fault_probability, early_warning, error_info, deviation } = charger;
  if (fault_probability < 0.40 && !early_warning) return null;
  const breach = deviation?.breaches?.length > 0
    ? deviation.breaches.reduce((a, b) => b.sigmas > a.sigmas ? b : a)
    : null;
  let cause = null, action = null, breachContext = null;
  if (error_info) {
    const code = error_info.code;
    if (DC_DC_CODES.has(code)) {
      cause  = "DC:DC power module failure";
      action = "Inspect backplane connections and DC:DC module. May require module replacement.";
    } else if (THERMAL_CODES.has(code)) {
      cause  = "Thermal management system fault";
      action = "Check cooling system, pump, and cable temperature sensors. Inspect for blocked airflow or coolant leak.";
    } else if (INVERTER_CODES.has(code)) {
      cause  = "Inverter or power supply communication failure";
      action = "Inspect master inverter communications. Check 12V/24V PSU. May require inverter restart or replacement.";
    } else if (isGridCode(code)) {
      cause  = "Grid supply fault — possible site power issue";
      action = "Check 3-phase grid supply voltage and frequency at site. This may be a site infrastructure issue, not the charger.";
    } else if (CONNECTOR_CODES.has(code)) {
      cause  = "Charge cable or connector degradation";
      action = "Inspect CCS connector and charge cable for physical damage, corrosion, or loose proximity pilot connection.";
    } else if (ISOLATION_CODES.has(code)) {
      cause  = "Insulation monitoring or contactor fault";
      action = "Inspect DC output contactors and insulation monitoring unit. Check for moisture ingress or wiring fault.";
    } else if (PHYSICAL_CODES.has(code)) {
      cause  = "Physical safety event detected";
      action = "Inspect charger for vandalism, door tampering, tilt, or gas/liquid sensor trip. Site visit required before restart.";
    } else {
      cause  = (error_info.description || "Unknown error").slice(0, 80);
      action = "Review error history and inspect charger hardware.";
    }
  }
  if (breach && deviation.max_sigma > 5) {
    const feat = breach.feature;
    if (feat.includes("fault_rate"))
      breachContext = `Fault rate is ${breach.sigmas.toFixed(1)}σ above this charger's normal baseline.`;
    else if (feat.includes("thermal"))
      breachContext = `Thermal events are ${breach.sigmas.toFixed(1)}σ above baseline — elevated heat stress detected.`;
    else if (feat.includes("session"))
      breachContext = `Session success rate has dropped significantly below this charger's historical baseline.`;
    else
      breachContext = `${feat.replace(/_/g, " ")} is ${breach.sigmas.toFixed(1)}σ above baseline.`;
  }
  if (!cause && !breachContext) return null;
  if (!cause) { cause = "Anomalous behavior detected"; action = "Review charger history and inspect hardware."; }
  return { cause, action, breachContext };
}

// ── Derating / flapping style maps ────────────────────────────────────────────

export const DERATING_STYLE = {
  CRITICAL: { bg: "#FEF2F2", border: "#EF4444", color: "#EF4444", cls: "dp-card-danger" },
  HIGH:     { bg: "#FFF7ED", border: "#F97316", color: "#F97316", cls: "dp-card-warning" },
  MODERATE: { bg: "#FFFBEB", border: "#F59E0B", color: "#F59E0B", cls: "dp-card-amber" },
};

export const FLAP_STYLE = {
  severe:       { bg: "#2D0A0A", border: "#FF3B3B", color: "#FF3B3B" },
  active:       { bg: "#2D1A00", border: "#FF8C00", color: "#FF8C00" },
  intermittent: { bg: "#2D2600", border: "#FFD700", color: "#FFD700" },
};

// ── Component map (parts replacement prediction) ──────────────────────────────

export const COMPONENT_MAP = {
  dc_dc:     ["DC:DC power module", "Backplane connections"],
  thermal:   ["DC:DC module", "Cooling fan assembly", "Coolant pump"],
  inverter:  ["Master inverter", "12V/24V PSU"],
  grid:      ["Site supply breaker", "Grid connection cables"],
  connector: ["CCS connector", "Charge cable"],
  isolation: ["DC output contactors", "Insulation monitoring unit"],
  physical:  ["Door sensors", "Emergency stop button"],
  default:   ["High-voltage components — bring standard kit"],
};

export const getComponents = (charger) => {
  const code = charger.error_info?.code;
  if (DC_DC_CODES.has(code))     return COMPONENT_MAP.dc_dc;
  if (THERMAL_CODES.has(code))   return COMPONENT_MAP.thermal;
  if (INVERTER_CODES.has(code))  return COMPONENT_MAP.inverter;
  if (isGridCode(code))          return COMPONENT_MAP.grid;
  if (CONNECTOR_CODES.has(code)) return COMPONENT_MAP.connector;
  if (ISOLATION_CODES.has(code)) return COMPONENT_MAP.isolation;
  if (PHYSICAL_CODES.has(code))  return COMPONENT_MAP.physical;
  if (charger.derating_alert)    return COMPONENT_MAP.dc_dc;
  return COMPONENT_MAP.default;
};

// ── Work order helpers ────────────────────────────────────────────────────────

export const WO_CAT_STYLE = {
  "Replace Parts (new)":  { bg: "#FEE2E2", color: "#991B1B" },
  "Replace Parts (used)": { bg: "#FEE2E2", color: "#991B1B" },
  "Repair":               { bg: "#FFF7ED", color: "#92400E" },
  "Powercycle":           { bg: "#EFF6FF", color: "#1D4ED8" },
  "Troubleshooting":      { bg: "#F3F4F6", color: "#374151" },
  "Signal Issues":        { bg: "#F3F4F6", color: "#374151" },
};

export const woStatusColor = (status) => {
  if (!status) return "#D97706";
  const s = status.toLowerCase();
  if (s.includes("complet")) return "#16A34A";
  if (s.includes("cancel") || s.includes("closed")) return "#9CA3AF";
  return "#D97706";
};

export const fmtWoDate = (d) => {
  if (!d) return null;
  const dt = /T|\s\d{2}:/.test(String(d)) ? new Date(d) : new Date(d + "T12:00:00");
  if (isNaN(dt.getTime())) return null;
  return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

// ── Timestamp utilities ───────────────────────────────────────────────────────

export const formatLastSeen = (ts) => {
  if (!ts || ts === "null") return null;
  try {
    return new Date(ts).toLocaleString("en-US", {
      month: "long", day: "numeric", year: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true,
    });
  } catch { return ts; }
};

// Normalises Python isoformat ("2024-01-15T10:30:00Z" or "+00:00") and SQLite
// CURRENT_TIMESTAMP ("2024-01-15 10:30:00") so Date.parse works in all browsers.
export const _parseTs = (ts) => {
  if (!ts) return NaN;
  const s = String(ts).trim().replace(" ", "T");
  const norm = s.endsWith("Z") || s.includes("+") || s.includes("-", 10) ? s : s + "Z";
  return new Date(norm).getTime();
};

export const timeAgo = (ts) => {
  const t = _parseTs(ts);
  if (!t || isNaN(t)) return "—";
  const diff = Date.now() - t;
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} hour${hr > 1 ? "s" : ""} ago`;
  const d = Math.floor(hr / 24);
  return `${d} day${d > 1 ? "s" : ""} ago`;
};

export const fmtAlertDate = (ts) => {
  const t = _parseTs(ts);
  if (!t || isNaN(t)) return "";
  const d = new Date(t);
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" }) +
    " at " + d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true });
};

// ── Leaflet dynamic loader ────────────────────────────────────────────────────

export const LEAFLET_CSS = "https://unpkg.com/leaflet/dist/leaflet.css";
export const CLUSTER_CSS = [
  "https://unpkg.com/leaflet.markercluster/dist/MarkerCluster.css",
  "https://unpkg.com/leaflet.markercluster/dist/MarkerCluster.Default.css",
];
export const LEAFLET_JS = "https://unpkg.com/leaflet/dist/leaflet.js";
export const CLUSTER_JS = "https://unpkg.com/leaflet.markercluster/dist/leaflet.markercluster.js";

export function loadCSS(href) {
  if (document.querySelector(`link[href="${href}"]`)) return;
  const el = document.createElement("link");
  el.rel = "stylesheet"; el.href = href;
  document.head.appendChild(el);
}

export function loadScript(src, isReady) {
  return new Promise(resolve => {
    if (isReady && isReady()) return resolve();
    if (document.querySelector(`script[src="${src}"]`)) {
      if (!isReady) return resolve();
      const t = setInterval(() => { if (isReady()) { clearInterval(t); resolve(); } }, 30);
      return;
    }
    const el = document.createElement("script");
    el.src = src; el.onload = resolve;
    document.head.appendChild(el);
  });
}

// ── Map marker & filter helpers ───────────────────────────────────────────────

export function markerStyle(c) {
  if (c.is_muted)
    return { color: "#9CA3AF", radius: 6, className: "dp-marker dp-marker-muted" };
  if (c.alert_type === "EARLY_WARNING")
    return { color: "#8B5CF6", radius: 8, className: "dp-marker dp-marker-ew" };
  const tier = c.priority_tier?.slice(0, 2);
  if (tier === "P1" || c.alert_type === "CONFIRMED_FAULT")
    return { color: "#EF4444", radius: 9, className: "dp-marker dp-marker-p1" };
  if (tier === "P2")
    return { color: "#F97316", radius: 8, className: "dp-marker dp-marker-p2" };
  if (tier === "P3")
    return { color: "#F59E0B", radius: 8, className: "dp-marker dp-marker-p3" };
  return { color: "#0EA5E9", radius: 7, className: "dp-marker dp-marker-p4" };
}

export function chargerMatchesFilter(c, filter) {
  if (!filter || filter === "ALL")        return true;
  if (filter === "MUTED")                  return !!c.is_muted;
  if (filter === "DERATING")               return !!c.derating_alert && !c.is_muted;
  if (filter === "EW")                     return c.alert_type === "EARLY_WARNING" && !c.is_muted;
  if (filter === "SILENT_RISK")            return (c.silent_risk_score ?? 0) >= 0.40 && (c.fault_probability ?? 1) < 0.40 && !c.is_muted;
  if (filter === "PROACTIVE_MAINTENANCE")  return (c.bilstm_parts_prob ?? 0) >= 0.50 && !c.is_muted;
  if (filter === "FLAPPING")               return !!c.is_flapping && !c.is_muted;
  return c.priority_tier?.startsWith(filter) && !c.is_muted;
}
